import { useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { BrandMark, PRIMARY_CTA_LABEL, PRIMARY_CTA_PATH } from './Brand';

const navLinkClass =
  'cursor-pointer font-sans text-sm text-slate-400 transition-all duration-200 hover:text-cyan-300';

const navLinkActiveClass = 'text-cyan-300';

const NAV_LINKS = [
  { to: '/#how-it-works', label: 'How it works' },
  { to: '/#coverage', label: 'Coverage' },
  { to: '/#sample-filing', label: 'Sample filing' },
  { to: '/contact', label: 'Contact' },
] as const;

function isHashLink(to: string): boolean {
  return to.includes('#');
}

/**
 * Global fixed application navbar - identical across marketing and portal pages.
 * Mobile: logo + hamburger only. CTA lives inside the menu.
 * Desktop (md+): full links + login + primary CTA; no hamburger.
 */
export function SiteNav({ onPrimaryCta }: { onPrimaryCta?: () => void }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const primaryClass =
    'btn-primary w-full shrink-0 cursor-pointer px-4 py-2.5 text-center text-sm sm:w-auto';

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  function closeMenu() {
    setMenuOpen(false);
  }

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 border-b border-slate-800 bg-[#0B0F17]/95 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-3 px-4 md:h-auto md:gap-6 md:py-5">
          <BrandMark showSubtitle={false} />

          <nav aria-label="Primary" className="hidden items-center gap-8 md:flex">
            {NAV_LINKS.map((link) =>
              isHashLink(link.to) ? (
                <Link key={link.to} to={link.to} className={navLinkClass}>
                  {link.label}
                </Link>
              ) : (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className={({ isActive }) =>
                    `${navLinkClass} ${isActive ? navLinkActiveClass : ''}`
                  }
                >
                  {link.label}
                </NavLink>
              ),
            )}
          </nav>

          <div className="flex shrink-0 items-center gap-2">
            <div className="hidden items-center gap-2 md:flex">
              {onPrimaryCta ? (
                <button type="button" onClick={onPrimaryCta} className={primaryClass}>
                  {PRIMARY_CTA_LABEL}
                </button>
              ) : (
                <Link to={PRIMARY_CTA_PATH} className={primaryClass}>
                  {PRIMARY_CTA_LABEL}
                </Link>
              )}
            </div>

            <button
              type="button"
              className="inline-flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-md border border-slate-800 text-slate-200 transition hover:border-cyan-500/40 hover:text-cyan-300 md:hidden"
              aria-expanded={menuOpen}
              aria-controls="mobile-nav"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setMenuOpen((open) => !open)}
            >
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {menuOpen ? (
          <div
            id="mobile-nav"
            className="max-h-[calc(100dvh-3.5rem)] overflow-y-auto border-t border-slate-800 bg-[#0B0F17] px-4 py-4 md:hidden"
          >
            <nav aria-label="Mobile" className="flex flex-col gap-1">
              {NAV_LINKS.map((link) =>
                isHashLink(link.to) ? (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="cursor-pointer rounded-md px-3 py-3 font-sans text-sm text-slate-300 hover:bg-slate-900 hover:text-cyan-300"
                    onClick={closeMenu}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    className={({ isActive }) =>
                      `cursor-pointer rounded-md px-3 py-3 font-sans text-sm hover:bg-slate-900 ${
                        isActive ? 'text-cyan-300' : 'text-slate-300 hover:text-cyan-300'
                      }`
                    }
                    onClick={closeMenu}
                  >
                    {link.label}
                  </NavLink>
                ),
              )}
            </nav>
            <div className="mt-3 border-t border-slate-800 pt-3">
              {onPrimaryCta ? (
                <button
                  type="button"
                  onClick={() => {
                    closeMenu();
                    onPrimaryCta();
                  }}
                  className={primaryClass}
                >
                  {PRIMARY_CTA_LABEL}
                </button>
              ) : (
                <Link to={PRIMARY_CTA_PATH} className={primaryClass} onClick={closeMenu}>
                  {PRIMARY_CTA_LABEL}
                </Link>
              )}
            </div>
          </div>
        ) : null}
      </header>
      {/* Spacer so page content clears the fixed header */}
      <div className="h-14 shrink-0 md:h-[4.75rem]" aria-hidden />
    </>
  );
}
